import React, { useMemo } from 'react';
import { FlagType, LimitType } from '../../../../../../@types/product.enum';
import HtmlContent from '../../../../../components-legacy/HtmlContent';
import useContext from '../../../../../hooks/useContext';
import useFlagBadgeStyle from '../../../../../hooks/useFlagBadgeStyle';
import useToggle from '../../../../../hooks/useToggle';
import Link from '../../../../../Router/Link';
import Badge from '../../../../../styled/Badge';
import { ReadMore } from '../../../../../styled/Badge/wrappers';
import Distance from '../../../../../styled/Distance';
import Panel from '../../../../../styled/Panel';
import { useFlags } from '../../providers/FlagsProvider';
import { useIsAuthorized } from '../../../../../providers/member';
import { ProductContext } from '../../providers/ProductProvider';
import { useTrackGenericEvent } from '../../../../../hooks/tracking/useTrackingEvents';

const clickEvent = { event: 'product_flag_click' };

const resolveBadgeType = (
  hasMainBadge: boolean,
  hasDiscount: boolean,
  showDynamicPercentage: boolean,
  hasBannerText: boolean,
) => {
  if (!hasMainBadge && hasDiscount) {
    return 'autoMainBadge';
  }
  if (!hasMainBadge) {
    return 'missingMainBadge';
  }
  if (showDynamicPercentage && hasBannerText) {
    return 'textDynamicMainBadge';
  }
  if (showDynamicPercentage && hasDiscount) {
    return 'dynamicMainBadge';
  }
  if (hasMainBadge && !showDynamicPercentage) {
    return 'normalMainBadge';
  }
  return '';
};

const GetMainBadgeType = (mainBadge?: IFlag, price?: IPrice) => {
  const currentMainBadge = useMemo(
    () =>
      resolveBadgeType(
        !!mainBadge,
        !!price?.discountPercentage,
        !!mainBadge?.productBanner?.showDynamicPercentage,
        !!mainBadge?.productBanner?.text,
      ),
    [
      mainBadge,
      price?.discountPercentage,
      mainBadge?.productBanner?.showDynamicPercentage,
      mainBadge?.productBanner?.text,
    ],
  );
  return currentMainBadge;
};

const MainBadge = () => {
  const trackClick = useTrackGenericEvent(clickEvent);
  const isAuthorized = useIsAuthorized();

  const mainFlag = useFlags(FlagType.main);
  const outletFlag = useFlags(FlagType.outlet);

  const flag = mainFlag ? mainFlag : outletFlag;

  const { price } = useContext(ProductContext);

  const [showInfo, toggleShowInfo] = useToggle(false);
  const mainBadgeStyle = useFlagBadgeStyle(flag);

  const currentMainBadge = GetMainBadgeType(flag, price);
  const discountPercentage =
    price?.discountPercentage && currentMainBadge !== 'normalMainBadge'
      ? ` -${price.discountPercentage}%`
      : '';

  const showDialog =
    flag?.productBanner?.moreInfo?.type === 'dialog' ||
    (flag?.productBanner?.moreInfo?.type === 'dialog-login-conditional' && !isAuthorized);

  const label = `${flag?.productBanner?.text || ''} ${discountPercentage || ''}`;
  const defaultColorScheme = currentMainBadge === 'autoMainBadge' ? true : false;
  const missingBannerText =
    !flag?.productBanner?.moreInfo.label &&
    !flag?.productBanner?.dateInfo &&
    !flag?.productBanner?.text;

  const formatOfferLimitDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getDate()}/${date.getMonth() + 1}`;
  };

  const offerLimitPresentation = useMemo(() => {
    if (flag?.offerLimit?.type === LimitType.Date && flag?.offerLimit?.value) {
      return `t.o.m. ${formatOfferLimitDate(flag.offerLimit.value)}`;
    } else if (flag?.offerLimit?.type === LimitType.Stock) {
      return 'Begränsat antal';
    }
    return '';
  }, [flag?.offerLimit]);

  if (currentMainBadge === 'missingMainBadge' || !currentMainBadge) {
    return null;
  }

  return (
    <>
      <Badge
        buttonText={showDialog ? flag?.productBanner?.moreInfo.label : undefined}
        collapsed="mobile"
        large
        fitToContent={missingBannerText}
        defaultColorScheme={defaultColorScheme}
        label={label}
        onClick={showDialog ? toggleShowInfo : undefined}
        tail={offerLimitPresentation || flag?.productBanner?.dateInfo}
        style={mainBadgeStyle}
        renderLink={
          flag?.productBanner?.moreInfo?.type === 'link' ||
          (flag?.productBanner?.moreInfo?.type === 'link-login-conditional' && !isAuthorized)
            ? () => (
                <Link
                  Component={ReadMore}
                  as="a"
                  href={flag.productBanner?.moreInfo.data}
                  onBeforeNavigate={trackClick}>
                  {flag.productBanner?.moreInfo.label}
                </Link>
              )
            : undefined
        }
      />

      <Distance className="size-1" />

      {showInfo && (
        <Panel onClose={toggleShowInfo}>
          <HtmlContent markdown={flag?.productBanner?.moreInfo.data} />
        </Panel>
      )}
    </>
  );
};

export default MainBadge;
